// @flow
import React, { useEffect, useState, useRef, useCallback } from 'react';
import styles from '~/_base/styles/_insightsChatView.scss';
import {
  DefaultSpeechRecognition,
  SpeechRecognitionShim,
} from '~/_base/classes/speechRecognition';
import ApeHostNotifications from '~/_base/functions/apeHostNotifications';
import { t } from 'i18next';
import { ILoader } from './helixIcons';

type Props = {
  disableQueryPreview: boolean,
  platform: string,
};

// Site assistant for UWP & Android
// As of 23/8/19 UWP currently uses a shim, while Android will attempt to
// use the native browsers (webview) SpeechRecognition API. On newer versions
// of Android, using the native SpeechRecognition API requires app permissions
// beforehand,  so we need to make an initial permission request before calling start().
// If that fails (i.e. the user is on an old Android version), we fall back to the shim
export default function InsightsChatViewEntry(props: Props) {
  const queryInputRef = useRef();
  const [queryPreviewText, setQueryPreviewText] = useState('');
  const [listenState, setListenState] = useState('finished');
  const [readyToRequestAudio, setReadyToRequestAudio] = useState(false);
  const speechApi = useRef(createSpeechApi(DefaultSpeechRecognition));
  const [entryMode, setEntryMode] = useState(
    speechApi.current ? 'voice' : 'text'
  );

  const startListening = useCallback(() => {
    if (speechApi.current && listenState === 'finished') {
      setListenState('starting');
      speechApi.current.start();
    }
  }, [listenState]);

  useEffect(() => {
    ApeHostNotifications.notify({ type: 'readyForSpeechRecognition' });
    window.onAudioPermissionChange = value => {
      if (value === true) {
        startListening();
      } else {
        alert(t('Unable to start speech recognition.'));
      }
    };
    window.readyToRequestAudioPermission = () => {
      setReadyToRequestAudio(true);
    };
    window.onAudioPermissionChange = value => {
      if (value === true) {
        startListening();
      } else {
        alert(t('Unable to start speech recognition.'));
      }
    };

    return () => {
      window.readyToRequestAudioPermission = undefined;
      window.onAudioPermissionChange = undefined;
    };
  }, [startListening]);

  function onSpeechResults(results) {
    const firstResult = results && results.length > 0 ? results[0] : null;

    if (firstResult && firstResult.isFinal) {
      setQueryPreviewText(firstResult.transcript);
      submitVoice(firstResult.transcript, results);

      // ideally we want to hide this once the chat bubble appears. Can't work out how to
      // get a notification for this, so just hide the preview text after some delay
      setTimeout(() => setQueryPreviewText(''), 350);
    } else {
      firstResult && setQueryPreviewText(firstResult.transcript + '...');
    }
  }

  function createSpeechApi(SpeechConstructor) {
    let speech = new SpeechConstructor();
    speech.interimResults = true;
    speech.maxAlternatives = 3;

    // don't specifiy language here - just default to current locale
    // speech.lang = 'en-AU';

    speech.onstart = () => setListenState('listening');
    speech.onend = () => setListenState('finished');

    speech.onerror = e => {
      if (e.error === 'not-allowed') {
        // This only occurs it the native SpeechRecognition fails,
        // so we fall back to the shim and restart the service
        speechApi.current = createSpeechApi(SpeechRecognitionShim);
        speechApi.current.start();
      } else {
        setListenState('finished');
        alert(`Error:  ${e.error} ${e.message}`);
      }
    };

    speech.onresult = e => {
      if (Array.isArray(e)) {
        // this probably comes from the android shim which gives a simpler style of
        // results
        return onSpeechResults(e);
      }

      // get the most recent result (seems to be zero in all actual implementations)
      const topResults = e.results[e.resultIndex || 0];

      const results = Array.from(topResults).map(a => {
        return {
          transcript: a.transcript,
          confidence: a.confidence,
          isFinal: topResults.isFinal,
        };
      });

      onSpeechResults(results);
    };

    return speech;
  }

  function toggleListening() {
    if (listenState === 'listening') {
      stopListening();
    } else {
      startListening();
    }
  }

  function handleStartListening() {
    if (props.platform === 'android' && readyToRequestAudio === true) {
      ApeHostNotifications.notify({ type: 'requestForRecordAudioPermission' });
    } else {
      startListening();
    }
  }

  function toggleMode() {
    const newMode = entryMode === 'text' ? 'voice' : 'text';
    setEntryMode(newMode);
  }

  function stopListening() {
    if (speechApi.current && listenState !== 'finished') {
      speechApi.current.stop();
    }
  }

  function handleSubmit(e) {
    try {
      e.preventDefault();

      if (queryInputRef.current) {
        const queryText = queryInputRef.current.value || '';
        queryInputRef.current.value = '';
        submitText(queryText);
      }
    } catch (e) {
      console.log(e);
    }
  }

  function submitText(queryText) {
    window.ApeInsightsChatViewProxy.pushQueryFromKeyboard(queryText);
  }

  function submitVoice(queryText, alternatives) {
    window.ApeInsightsChatViewProxy.pushQueryFromDictation(
      queryText,
      alternatives
    );
  }

  function renderKeyboardActions() {
    return (
      <form className="keyboard-actions" onSubmit={handleSubmit}>
        <div className="actions-row">
          <input
            type="text"
            placeholder={t('Ask a question...')}
            ref={queryInputRef}
          />
          <a
            className="icon-button small mic"
            href="javascript:void(0)"
            onClick={e => toggleMode()}
          />
        </div>
      </form>
    );
  }

  function renderVoiceActions() {
    return (
      <div className="voice-actions actions-row">
        <div className="mic-row">
          <a
            className="icon-button round mic"
            href="javascript:void(0)"
            onTouchStart={e => handleStartListening()}
            onTouchEnd={e => stopListening()}
            onClick={e => (props.platform == 'uwp' ? toggleListening() : '')}
          >
            {listenState == 'starting' && <ILoader />}
          </a>

          {props.disableQueryPreview ? (
            ''
          ) : (
            <div className="query-preview">{queryPreviewText}</div>
          )}
        </div>

        <a
          className="icon-button small keyboard"
          href="javascript:void(0)"
          onClick={e => toggleMode()}
        />
      </div>
    );
  }

  const entryModeClass = entryMode === 'text' ? 'mode-text' : 'mode-voice';
  const listenStateClass = `voice-state-${listenState}`;
  if (entryMode === 'text') {
    setTimeout(
      () => queryInputRef.current && queryInputRef.current.focus(),
      120
    );
  }

  return (
    <div className={`entry ${entryModeClass} ${listenStateClass}`}>
      {entryMode === 'text' ? renderKeyboardActions() : renderVoiceActions()}
    </div>
  );
}
